import React, { useEffect, useRef, useState } from "react";
import { Card, notification } from "antd";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import styled from "styled-components";
import "./TableBlot";
import CustomToolbar from "./toolbar";
import { useLocation } from "react-router-dom";
import { useUserGuide } from "./user-guide-context.jsx";
import ImageFormatModule from "./ImageFormat.js";
import CustomImageBlot from "./ImageBlot.js"; // ✅ Import custom blot
import { getUserGuideById } from "../../Services/userGuideServices";
import { getImageURL } from "../../Services/deckleMatchServices";

// Helper function to replace image keys with fresh presigned URLs when loading HTML
const replaceImageKeysWithUrls = async (htmlContent, pageId) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");
  const images = doc.querySelectorAll("img[src]");
  
  const imagePromises = [];
  
  images.forEach((img) => {
    const src = img.getAttribute("src");
    
    // Check if it's an image key placeholder
    if (src && src.startsWith("{{IMAGE_KEY:")) {
      const bucketKey = src.replace("{{IMAGE_KEY:", "").replace("}}", "");
      imagePromises.push(
        getImageURL({
          bucket_key: bucketKey,
          operation: "download",
        })
          .then((downloadUrl) => {
            img.setAttribute("src", downloadUrl);
          })
          .catch((error) => {
            console.error(`Error getting presigned URL for ${bucketKey}:`, error);
          })
      );
    } else if (src && src.startsWith("userguide/") && !src.includes("?") && !src.startsWith("http")) {
      // Handle legacy format (direct bucket key without placeholder)
      imagePromises.push(
        getImageURL({
          bucket_key: src,
          operation: "download",
        })
          .then((downloadUrl) => {
            img.setAttribute("src", downloadUrl);
          })
          .catch((error) => {
            console.error(`Error getting presigned URL for ${src}:`, error);
          })
      );
    }
  });
  
  await Promise.all(imagePromises);
  return doc.documentElement.outerHTML;
};

// Fonts
const Font = Quill.import("formats/font");
Font.whitelist = ["manrope", "sans-serif", "serif"];
Quill.register(Font, true);

// Sizes
const Size = Quill.import("formats/size");
Size.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px"];
Quill.register(Size, true);

// Extra modules
Quill.register("modules/imageFormat", ImageFormatModule);

// Undo/Redo handlers
const undo = () => {
  const editor = window.quillRef;
  if (editor && editor.history) editor.history.undo();
};
const redo = () => {
  const editor = window.quillRef;
  if (editor && editor.history) editor.history.redo();
};

// Styled Components
const EditorContainer = styled.div`
  width: 100%;
`;

const CardWrapper = styled(Card)`
  margin-bottom: 1%;
  .ant-card-body {
    padding: 0;
  }
`;

const QuillEditorWrapper = styled.div`
  .ql-container {
    height: 100%;
    display: block !important;
  }

  .ql-editor {
    direction: ltr;
    text-align: left;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: "Manrope", sans-serif;
    line-height: 1.5;
    min-height: 80vh;
    padding: 25px;
    outline: none;
  }

  .ql-size-12px {
    font-size: 12px;
  }
  .ql-size-14px {
    font-size: 14px;
  }
  .ql-size-16px {
    font-size: 16px;
  }
  .ql-size-18px {
    font-size: 18px;
  }
  .ql-size-24px {
    font-size: 24px;
  }
  .ql-size-32px {
    font-size: 32px;
  }
`;

// Custom format handlers that properly toggle formats on/off
const createToggleHandler = (formatName) => {
  return function(value) {
    const editor = this.quill;
    if (!editor) return;
    
    const range = editor.getSelection(true);
    if (!range) return;
    
    // Check if format is already applied
    const format = editor.getFormat(range);
    const isActive = format[formatName];
    
    if (isActive) {
      // Format is active, remove it
      editor.format(formatName, false, "user");
    } else {
      // Format is not active, apply it
      editor.format(formatName, true, "user");
    }
  };
};

// Quill Modules
const modules = {
  toolbar: {
    container: "#toolbar",
    handlers: {
      undo,
      redo,
      bold: createToggleHandler("bold"),
      italic: createToggleHandler("italic"),
      underline: createToggleHandler("underline"),
      strike: createToggleHandler("strike"),
      insertTable: function () {
        const editor = window.quillRef;
        if (!editor) return;
        const range = editor.getSelection(true);
        const index = range ? range.index : editor.getLength();
        const uniqueId = `custom-table-${Date.now()}-${
          crypto.getRandomValues(new Uint32Array(1))[0]
        }`;
        const value = { id: uniqueId, data: [], columns: [] };
        editor.insertEmbed(index, "customTable", value, "user");
        editor.insertText(index + 1, "\n", "user");
        editor.setSelection(index + 2, 0);
      },
      image: function () {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();

        input.onchange = () => {
          const file = input.files?.[0];
          if (!file) return;

          const editor = this.quill; // ✅ safer than window.quillRef
          const range = editor.getSelection(true);
          const url = URL.createObjectURL(file);

          editor.insertEmbed(
            range?.index ?? editor.getLength(),
            "customImage", // ✅ must match blotName
            {
              url,
              data_key: "",
              width: "200px",
              float: "none",
              display: "block",
              margin: "0",
            },
            "user"
          );

          editor.setSelection((range?.index ?? editor.getLength()) + 1);
        };
      },
    },
  },
  imageFormat: {},
  history: { delay: 1000, maxStack: 100, userOnly: true },
};

// Quill Formats
const formats = [
  "font",
  "size",
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "list",
  "indent",
  "align",
  "link",
  "image",
  "video",
  "customTable",
  "customImage", // ✅ include blot
];

export default function Editor({ edit, onChange, preview, onPlainTextChange, showToaster }) {
  const location = useLocation();
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const { state, handleUserGuideState } = useUserGuide();
  const selectedItem = state.selectedItem;
  const saveTimeoutRef = useRef(null);
  const quillRef = useRef(null);
  // Track what we've already loaded to prevent infinite loops
  const lastLoadedRef = useRef({ id: null, version: null });
  // Track if we're currently loading content to prevent false isChanged triggers
  const isLoadingContentRef = useRef(false);

  const setQuillRef = (instance) => {
    quillRef.current = instance;
    if (instance && typeof instance.getEditor === "function") {
      window.quillRef = instance.getEditor();
    }
  };

  // Fetch and load user guide content when a page is selected
  useEffect(() => {
    const loadUserGuideContent = async () => {
      // Only load content for pages
      if (!selectedItem?.page || !selectedItem?.title) {
        setValue("");
        onChange?.("");
        lastLoadedRef.current = { id: null, version: null };
        return;
      }
      
      // If the page ID changed, clear restoredVersion to use latest version
      if (lastLoadedRef.current.id && lastLoadedRef.current.id !== selectedItem.id) {
        // Page changed, clear restoredVersion if it exists
        if (selectedItem.restoredVersion) {
          handleUserGuideState({
            selectedItem: {
              ...selectedItem,
              restoredVersion: undefined,
            },
          });
        }
      }

      // Skip API call for newly created local items (items without uuid are local)
      // Local items are created in the UI and don't exist in the backend yet
      if (!selectedItem?.uuid) {
        // This is a newly created local item, just clear the editor
        setValue("");
        onChange?.("");
        lastLoadedRef.current = { id: null, version: null };
        return;
      }

      const currentId = selectedItem.id;
      const publishVersion = selectedItem.publish_version;
      
      // If there's a restoredVersion, use it; otherwise use the latest version from the object
      // Initially, use the latest version
      let currentVersion = "1.0.0";
      if (selectedItem.restoredVersion) {
        // User has restored a specific version
        currentVersion = selectedItem.restoredVersion;
      } else if (typeof publishVersion === 'object' && !Array.isArray(publishVersion)) {
        // Handle object structure: { "1.0.0": "draft", "1.0.1": "published" }
        const versions = Object.keys(publishVersion);
        if (versions.length > 0) {
          // Sort and get the latest version
          const sortedVersions = versions.sort((a, b) => {
            const aParts = a.split('.').map(Number);
            const bParts = b.split('.').map(Number);
            for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
              const aVal = aParts[i] || 0;
              const bVal = bParts[i] || 0;
              if (aVal !== bVal) return aVal - bVal;
            }
            return 0;
          });
          currentVersion = sortedVersions[sortedVersions.length - 1];
        }
      } else if (Array.isArray(publishVersion) && publishVersion.length > 0) {
        // Fallback for array (backward compatibility)
        currentVersion = publishVersion[publishVersion.length - 1];
      } else if (typeof publishVersion === 'string' && publishVersion) {
        // Fallback for string (backward compatibility)
        currentVersion = publishVersion;
      }

      // Check if we've already loaded this exact ID and version
      if (
        lastLoadedRef.current.id === currentId &&
        lastLoadedRef.current.version === currentVersion
      ) {
        // Already loaded this content, skip to prevent infinite loop
        return;
      }

      try {
        setLoading(true);
        isLoadingContentRef.current = true; // Mark that we're loading content
        const response = await getUserGuideById(currentId);

        if (response && response.data) {
          const userGuideData = response.data;
          let contentToLoad = "";

          // Use the version from selectedItem (which might be different from API response when restoring)
          const versionToLoad = currentVersion;
          const pageId = userGuideData.id;
          const htmlFileName = `${pageId}.html`;
          const htmlBucketKey = `user-guide-documents/${pageId}/${versionToLoad}/${htmlFileName}`;

          try {
            // Get presigned download URL for HTML file
            const downloadUrl = await getImageURL({
              bucket_key: htmlBucketKey,
              operation: "download",
            });

            // Fetch HTML content from S3
            const htmlResponse = await fetch(downloadUrl);
            if (htmlResponse.ok) {
              contentToLoad = await htmlResponse.text();
              
              // Replace image keys with fresh presigned URLs
              contentToLoad = await replaceImageKeysWithUrls(contentToLoad, pageId);
            } else {
              // If S3 file doesn't exist, fall back to content field
              throw new Error("HTML file not found in S3");
            }
          } catch (s3Error) {
            console.warn(
              "Could not load HTML from S3, using content field:",
              s3Error
            );
            // Fallback to content field if S3 file doesn't exist
            if (userGuideData.content) {
              // Content field contains plain text, convert to HTML
              contentToLoad = userGuideData.content
                .split("\n")
                .map((line) => (line.trim() ? `<p>${line}</p>` : "<p><br></p>"))
                .join("");
            }
          }

          // Set the content - ReactQuill will handle the update
          // Clear isChanged when loading content to prevent false positives
          handleUserGuideState({ isChanged: false });
          // Set value without triggering onChange to prevent false isChanged
          // We'll call onChange manually after a delay to ensure content is set
          setValue(contentToLoad);
          // Don't call onChange immediately - it will be called by ReactQuill's onChange
          // but our isLoadingContentRef will prevent handleEditorChange from setting isChanged

          // Wait a bit for the editor to be ready, then update plain text
          setTimeout(() => {
            if (quillRef.current?.getEditor) {
              const editor = quillRef.current.getEditor();
              const plainText = editor.getText();
              onPlainTextChange?.(plainText);
            }
            // Mark that we're done loading content after a short delay
            // This allows any onChange handlers triggered by setValue to complete
            setTimeout(() => {
              isLoadingContentRef.current = false;
            }, 500);
          }, 100);

          // Mark as loaded to prevent re-loading
          lastLoadedRef.current = { id: currentId, version: versionToLoad };

          // Only update selectedItem if the data actually changed
          // Preserve publish_version as object from API response
          // New format: { "1.0.0": {status: "draft", published_at: "2025-12-24 05:02:34"} }
          // Old format: { "1.0.0": "draft" }
          const apiPublishVersion = typeof userGuideData.publish_version === 'object' && !Array.isArray(userGuideData.publish_version)
            ? userGuideData.publish_version
            : (userGuideData.publish_version ? (typeof userGuideData.publish_version === 'string' ? { [userGuideData.publish_version]: { status: "published", published_at: null } } : { "1.0.0": { status: "draft", published_at: null } }) : { "1.0.0": { status: "draft", published_at: null } });
          
          // Check if we need to update (only if version object or content changed)
          const currentPublishVersion = typeof selectedItem.publish_version === 'object' && !Array.isArray(selectedItem.publish_version)
            ? selectedItem.publish_version
            : (selectedItem.publish_version ? (typeof selectedItem.publish_version === 'string' ? { [selectedItem.publish_version]: { status: "published", published_at: null } } : { "1.0.0": { status: "draft", published_at: null } }) : { "1.0.0": { status: "draft", published_at: null } });
          
          const needsUpdate =
            JSON.stringify(currentPublishVersion) !== JSON.stringify(apiPublishVersion) ||
            selectedItem.content !== userGuideData.content;

          if (needsUpdate) {
            handleUserGuideState({
              selectedItem: {
                ...selectedItem,
                ...userGuideData,
                publish_version: apiPublishVersion, // Keep as object from API
                content: userGuideData.content,
              },
            });
          }
        }
      } catch (error) {
        console.error("Error loading user guide content:", error);
        if (showToaster) {
          showToaster(error.message || "Failed to load user guide content", true);
        }
        // Clear editor on error
        setValue("");
        onChange?.("");
        lastLoadedRef.current = { id: null, version: null };
      } finally {
        setLoading(false);
        isLoadingContentRef.current = false; // Mark that we're done loading
        // Ensure isChanged is cleared even if there was an error
        handleUserGuideState({ isChanged: false });
        console.log("Content loading finished (finally block), isLoadingContentRef set to false");
      }
    };

    loadUserGuideContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedItem?.id,
    selectedItem?.title,
    selectedItem?.publish_version,
    selectedItem?.restoredVersion,
    location.pathname,
  ]);

  const handleEditorChange = (newValue) => {
    if (!quillRef.current?.getEditor) return;

    // Don't set isChanged if we're currently loading content
    if (isLoadingContentRef.current) {
      return;
    }

    const editor = quillRef.current.getEditor();
    const text = editor.getText();

    setValue(newValue);
    onChange?.(newValue);
    onPlainTextChange?.(text);

    handleUserGuideState({ isChanged: true });

    // Clear isChanged after 10 seconds for UI purposes (so publish button isn't disabled for 60 seconds)
    // Auto-save uses a separate ref (hasUnsavedChangesRef) to track unsaved changes
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      handleUserGuideState({ isChanged: false });
      saveTimeoutRef.current = null;
    }, 10000);
  };

  if (!selectedItem?.page) {
    return (
      <EditorContainer>
        <CardWrapper style={{ height: "95vh" }} />
      </EditorContainer>
    );
  }

  if (loading) {
    return (
      <EditorContainer>
        <CardWrapper
          style={{
            height: "95vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ color: "#64748B", fontSize: "14px" }}>
            Loading content...
          </div>
        </CardWrapper>
      </EditorContainer>
    );
  }

  return (
    // <EditorContainer>
    // <div className="editor-container">
    // {edit && !preview && (
    //   <CardWrapper style={{ height: "7vh" }}>
    //     <CustomToolbar />
    //   </CardWrapper>
    // )}
    // <CardWrapper
    //   style={{
    //     height: "max-content",
    //     marginTop: edit && !preview ? "1%" : 0,
    //   }}
    // >
    //   <QuillEditorWrapper>
    //     <ReactQuill
    //       ref={setQuillRef}
    //       value={value}
    //       onChange={handleEditorChange}
    //       modules={modules}
    //       formats={formats}
    //       bounds=".editor-container"
    //       style={{ height: "90vh" }}
    //       // onFocus={(e) => {
    //       //   if (quillRef.current) {
    //       //     const editor = quillRef.current.getEditor();
    //       //     editor.disable();
    //       //   }
    //       // }}
    //       readOnly={preview || !edit}
    //     />
    //   </QuillEditorWrapper>
    // </CardWrapper>
    // </div>
    <div style={{ position: "relative", height: "100%" }}>
      <CardWrapper
        style={{
          height: edit && !preview ? "7vh" : "0vh",
          border: edit && !preview ? "1px solid #f0f0f0" : "none",
          position: edit && !preview ? "sticky" : "relative",
          top: edit && !preview ? "0" : "auto",
          zIndex: edit && !preview ? 100 : "auto",
          backgroundColor: edit && !preview ? "#ffffff" : "transparent",
        }}
      >
        <div style={{ display: edit && !preview ? "block" : "none" }}>
          <CustomToolbar id="toolbar" />
        </div>
      </CardWrapper>

      <CardWrapper
        style={{
          height: "max-content",
          marginTop: edit && !preview ? "1%" : 0,
        }}
      >
        <QuillEditorWrapper>
          <ReactQuill
            ref={setQuillRef}
            value={value}
            onChange={handleEditorChange}
            modules={modules}
            formats={formats}
            style={{ height: "90vh" }}
            readOnly={preview || !edit}
          />
        </QuillEditorWrapper>
      </CardWrapper>
    </div>
    // </EditorContainer>
  );
}
